import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Send, CheckCircle, User, Phone, MapPin, Wallet } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

gsap.registerPlugin(ScrollTrigger);

import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const EnquiryForm = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    city: '',
    investment: ''
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.form-container',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, investment: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "enquiries"), {
        ...formData,
        createdAt: serverTimestamp()
      });

      try {
        await fetch('https://automate.nirosha.org/webhook/pizzatoons', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
      } catch (webhookError) {
        console.error("Webhook submission failed", webhookError);
      }

      setSubmitted(true);

      gsap.fromTo('.success-message',
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(1.7)' }
      );
    } catch (error) {
      console.error("Error adding document: ", error);
      alert(language === 'en' ? "Something went wrong. Please try again." : "काहीतरी चुकले. कृपया पुन्हा प्रयत्न करा.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="enquiry" ref={sectionRef} className="py-20 lg:py-32 bg-gradient-to-br from-red-600 via-red-500 to-yellow-500 dark:from-red-900 dark:via-red-800 dark:to-yellow-900 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="form-container bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden transition-colors duration-300">
          {!submitted ? (
            <>
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t.enquiry.title} <span className="text-yellow-400">{t.enquiry.highlight}</span>
                </h2>
                <p className="text-white/80" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t.enquiry.subtitle}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2 text-gray-700 dark:text-gray-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    <User className="w-4 h-4" /> {t.enquiry.nameLabel}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t.enquiry.namePlaceholder}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 border-gray-200 focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Mobile */}
                <div className="space-y-2">
                  <Label htmlFor="mobile" className="flex items-center gap-2 text-gray-700 dark:text-gray-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    <Phone className="w-4 h-4" /> {t.enquiry.mobileLabel}
                  </Label>
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    placeholder={t.enquiry.mobilePlaceholder}
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                    className="h-12 border-gray-200 focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* City */}
                <div className="space-y-2">
                  <Label htmlFor="city" className="flex items-center gap-2 text-gray-700 dark:text-gray-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    <MapPin className="w-4 h-4" /> {t.enquiry.cityLabel}
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    placeholder={t.enquiry.cityPlaceholder}
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="h-12 border-gray-200 focus:border-red-500 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                {/* Investment */}
                <div className="space-y-2">
                  <Label htmlFor="investment" className="flex items-center gap-2 text-gray-700 dark:text-gray-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    <Wallet className="w-4 h-4" /> {t.enquiry.investmentLabel}
                  </Label>
                  <Select onValueChange={handleSelectChange} required>
                    <SelectTrigger className="h-12 border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                      <SelectValue placeholder={t.enquiry.investmentPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {t.enquiry.investmentOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} style={{ fontFamily: "'Poppins', sans-serif" }}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-red-500 hover:bg-red-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  <Send className="w-5 h-5 mr-2" />
                  {isSubmitting ? t.enquiry.submittingBtn : t.enquiry.submitBtn}
                </Button>

                <p className="text-center text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t.enquiry.consent}
                </p>
              </form>
            </>
          ) : (
            <div className="success-message p-12 text-center">
              <div className="w-24 h-24 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {t.enquiry.successTitle}
              </h3>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {t.enquiry.successSubtitle.replace('! Our team', `! ${formData.name}, Our team`)}
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {t.enquiry.resetBtn}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EnquiryForm;
